import { utils } from 'near-api-js';
import { toNonDivisibleNumber } from '../utils/numbers';
import { executeMultipleTransactions, ONE_YOCTO_NEAR, wallet } from './near';
import getConfig, { univ4HookAllowed } from './config';
import { ftGetBalance, ftGetStorageBalance } from './ft-contract';
import { nearDepositTransaction } from './wrap-near';
import { getCurrentWallet } from '../utils/wallets-integration';

const Q96 = 1n << 96n;
const SHIFT128 = 1n << 128n;
/** Uniswap v4 dynamic-fee marker. The hook chooses the fee, so a quote cannot price the swap. */
const DYNAMIC_FEE_FLAG = 0x800000;
/** Inclusive bounds from the pool manager. A swap limit must sit strictly inside them. */
const MIN_SQRT_RATIO = 4295128739n;
const MAX_SQRT_RATIO = 1461446703485210103287273052203988822378723970342n;
const MULTIPLIERS = [
  'fff97272373d413259a46990580e213a',
  'fff2e50f5f656932ef12357cf3c7fdcc',
  'ffe5caca7e10e4e61c3624eaa0941cd0',
  'ffcb9843d60f6159c9db58835c926644',
  'ff973b41fa98c081472e6896dfb254c0',
  'ff2ea16466c96a3843ec78b326b52861',
  'fe5dee046a99a2a811c461f1969c3053',
  'fcbe86c7900a88aedcffc83b479aa3a4',
  'f987a7253ac413176f2b074cf7815e54',
  'f3392b0822b70005940c7a398e4b70f3',
  'e7159475a2c29b7443b29c7fa6e889d9',
  'd097f3bdfd2022b8845ad8f792aa5825',
  'a9f746462d870fdf8a65dc1f90e061e5',
  '70d869a156d2a1b890bb3df62baf32f7',
  '31be135f97d08fd981231505542fcfa6',
  '9aa508b5b7a84e1c677de54f3e99bc9',
  '5d6af8dedb81196699c329225ee604',
  '2216e584f5fa1ea926041bedfe98',
  '48a170391f7dc42444e8fa2',
];

export type ClmmPoolView = {
  pool_id: string;
  currency0: string;
  currency1: string;
  fee: number;
  tick_spacing: number;
  hooks: string | null;
  hook_permissions: number;
  sqrt_price_x96: string;
  tick: number;
  liquidity: string;
};

export function poolKeyOf(pool: ClmmPoolView) {
  return {
    currency0: pool.currency0,
    currency1: pool.currency1,
    fee: Number(pool.fee),
    tick_spacing: Number(pool.tick_spacing),
    hooks: pool.hooks ?? null,
    hook_permissions: Number(pool.hook_permissions || 0),
  };
}

export function sqrtRatioAtTick(tick: number): bigint {
  const abs = tick < 0 ? -tick : tick;
  let ratio =
    abs & 1 ? BigInt('0x' + 'fffcb933bd6fad37aa2d162d1a594001') : SHIFT128;
  for (let i = 0; i < MULTIPLIERS.length; i += 1) {
    if (abs & (1 << (i + 1))) {
      ratio = (ratio * BigInt('0x' + MULTIPLIERS[i])) >> 128n;
    }
  }
  if (tick > 0) {
    ratio = ((1n << 256n) - 1n) / ratio;
  }
  const remainder = ratio & ((1n << 32n) - 1n);
  let sqrt = ratio >> 32n;
  if (remainder !== 0n) sqrt += 1n;
  return sqrt;
}

function divRoundUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator + denominator - 1n) / denominator;
}

function amount0Delta(
  sqrtA: bigint,
  sqrtB: bigint,
  liquidity: bigint,
  roundUp: boolean
): bigint {
  if (sqrtA > sqrtB) {
    const swap = sqrtA;
    sqrtA = sqrtB;
    sqrtB = swap;
  }
  if (liquidity === 0n || sqrtA === sqrtB) return 0n;
  const numerator1 = liquidity << 96n;
  const numerator2 = sqrtB - sqrtA;
  if (roundUp) {
    const inner = divRoundUp(numerator1 * numerator2, sqrtB);
    return divRoundUp(inner, sqrtA);
  }
  return (numerator1 * numerator2) / sqrtB / sqrtA;
}

function amount1Delta(
  sqrtA: bigint,
  sqrtB: bigint,
  liquidity: bigint,
  roundUp: boolean
): bigint {
  if (sqrtA > sqrtB) {
    const swap = sqrtA;
    sqrtA = sqrtB;
    sqrtB = swap;
  }
  if (liquidity === 0n || sqrtA === sqrtB) return 0n;
  if (roundUp) return divRoundUp(liquidity * (sqrtB - sqrtA), Q96);
  return (liquidity * (sqrtB - sqrtA)) / Q96;
}

export function amountsForLiquidity(
  sqrtPrice: bigint,
  sqrtLower: bigint,
  sqrtUpper: bigint,
  liquidity: bigint,
  roundUp: boolean
): { amount0: bigint; amount1: bigint } {
  if (sqrtPrice <= sqrtLower) {
    return {
      amount0: amount0Delta(sqrtLower, sqrtUpper, liquidity, roundUp),
      amount1: 0n,
    };
  }
  if (sqrtPrice < sqrtUpper) {
    return {
      amount0: amount0Delta(sqrtPrice, sqrtUpper, liquidity, roundUp),
      amount1: amount1Delta(sqrtLower, sqrtPrice, liquidity, roundUp),
    };
  }
  return {
    amount0: 0n,
    amount1: amount1Delta(sqrtLower, sqrtUpper, liquidity, roundUp),
  };
}

export function maxLiquidityForAmounts(
  sqrtPrice: bigint,
  sqrtLower: bigint,
  sqrtUpper: bigint,
  amount0: bigint,
  amount1: bigint
): bigint {
  const fits = (liquidity: bigint) => {
    const cost = amountsForLiquidity(
      sqrtPrice,
      sqrtLower,
      sqrtUpper,
      liquidity,
      true
    );
    return cost.amount0 <= amount0 && cost.amount1 <= amount1;
  };
  if (!fits(1n)) return 0n;
  let high = 1n;
  while (fits(high)) {
    if (high > 1n << 120n) break;
    high *= 2n;
  }
  let low = 0n;
  while (low + 1n < high) {
    const mid = (low + high) / 2n;
    if (fits(mid)) low = mid;
    else high = mid;
  }
  return low;
}

export function defaultRange(tick: number, spacing: number) {
  const step = spacing || 1;
  const base = Math.floor(tick / step) * step;
  return { lower: base - step, upper: base + step * 2 };
}

function transfer(tokenId: string, receiverId: string, amount: string) {
  return {
    receiverId: tokenId,
    functionCalls: [
      {
        methodName: 'ft_transfer_call',
        args: {
          receiver_id: receiverId,
          amount,
          msg: '',
        },
        gas: '250000000000000',
        amount: ONE_YOCTO_NEAR,
      },
    ],
  };
}

function depositStorage(contractId: string) {
  return {
    receiverId: contractId,
    functionCalls: [
      {
        methodName: 'storage_deposit',
        args: {},
        amount: '0.05',
        gas: '30000000000000',
      },
    ],
  };
}

export async function addClmmLiquidity(options: {
  contractId: string;
  pool: ClmmPoolView;
  token0Decimals: number;
  token1Decimals: number;
  amount0: string;
  amount1: string;
  tickLower: number;
  tickUpper: number;
}) {
  const raw0 = BigInt(
    toNonDivisibleNumber(options.token0Decimals, options.amount0 || '0')
  );
  const raw1 = BigInt(
    toNonDivisibleNumber(options.token1Decimals, options.amount1 || '0')
  );
  const sqrtPrice = BigInt(options.pool.sqrt_price_x96);
  const sqrtLower = sqrtRatioAtTick(options.tickLower);
  const sqrtUpper = sqrtRatioAtTick(options.tickUpper);
  const liquidity = maxLiquidityForAmounts(
    sqrtPrice,
    sqrtLower,
    sqrtUpper,
    raw0,
    raw1
  );
  if (liquidity <= 0n) throw new Error('Amount is too small for this range');
  const transactions = [depositStorage(options.contractId)];
  await wrapShortfall(options.pool.currency0, raw0, transactions);
  await wrapShortfall(options.pool.currency1, raw1, transactions);
  if (raw0 > 0n) {
    transactions.push(
      transfer(options.pool.currency0, options.contractId, raw0.toString())
    );
  }
  if (raw1 > 0n) {
    transactions.push(
      transfer(options.pool.currency1, options.contractId, raw1.toString())
    );
  }
  transactions.push({
    receiverId: options.contractId,
    functionCalls: [
      {
        methodName: 'modify_liquidity',
        args: {
          key: poolKeyOf(options.pool),
          tick_lower: options.tickLower,
          tick_upper: options.tickUpper,
          liquidity_delta: liquidity.toString(),
          salt: '',
          amount0_max: raw0.toString(),
          amount1_max: raw1.toString(),
        },
        gas: '200000000000000',
        amount: '0',
      },
    ],
  });
  await executeMultipleTransactions(
    transactions,
    `${window.location.origin}${window.location.pathname}`
  );
  return liquidity.toString();
}

let clmmPoolCache: { at: number; pools: ClmmPoolView[] } | null = null;

export async function listClmmPools(): Promise<ClmmPoolView[]> {
  const contractId = getConfig().UNIV4_CONTRACT_ID;
  if (!contractId) return [];
  if (clmmPoolCache && Date.now() - clmmPoolCache.at < 20000) {
    return clmmPoolCache.pools;
  }
  const pools: ClmmPoolView[] = await wallet
    .account()
    .viewFunction(contractId, 'get_pools', { from_index: 0, limit: 100 });
  clmmPoolCache = { at: Date.now(), pools: pools || [] };
  return clmmPoolCache.pools;
}

function isqrt(value: bigint): bigint {
  if (value < 2n) return value;
  let x = value;
  let y = (x + 1n) / 2n;
  while (y < x) {
    x = y;
    y = (x + value / x) / 2n;
  }
  return x;
}

/** Furthest price a swap may reach and still pass the contract's limit check. */
export function extremeSqrtPriceLimit(zeroForOne: boolean): string {
  return (zeroForOne ? MIN_SQRT_RATIO + 1n : MAX_SQRT_RATIO - 1n).toString();
}

/** Worst sqrt price the quoted swap may reach, moved by `slippageBps` past the quote. */
export function sqrtPriceLimit(options: {
  startSqrt: string;
  endSqrt: string;
  zeroForOne: boolean;
  slippageBps: bigint;
}): string {
  const start = BigInt(options.startSqrt || '0');
  const end = BigInt(options.endSqrt || '0');
  const floor = MIN_SQRT_RATIO + 1n;
  const ceiling = MAX_SQRT_RATIO - 1n;
  if (start <= floor || start >= ceiling || end <= 0n) {
    throw new Error('Pool price is outside the swap range');
  }
  const bps =
    options.slippageBps < 0n
      ? 0n
      : options.slippageBps > 9_999n
      ? 9_999n
      : options.slippageBps;
  const scale = 1n << 64n;
  const keep = 10_000n - bps;
  const factor = isqrt((keep * scale * scale) / 10_000n);
  let limit = options.zeroForOne
    ? (end * factor) / scale
    : factor === 0n
    ? end
    : (end * scale) / factor;
  if (options.zeroForOne) {
    if (limit >= start) limit = start - 1n;
    if (limit < floor) limit = floor;
    if (limit >= start)
      throw new Error('Slippage leaves no room below the current price');
  } else {
    if (limit <= start) limit = start + 1n;
    if (limit > ceiling) limit = ceiling;
    if (limit <= start)
      throw new Error('Slippage leaves no room above the current price');
  }
  return limit.toString();
}

export async function quoteClmmPair(options: {
  tokenInId: string;
  tokenOutId: string;
  amountInRaw: string;
}): Promise<{
  contractId: string;
  pool: ClmmPoolView;
  amountOutRaw: string;
  sqrtPriceAfter: string;
} | null> {
  const contractId = getConfig().UNIV4_CONTRACT_ID;
  if (!contractId || !options.amountInRaw || options.amountInRaw === '0') {
    return null;
  }
  const pools = await listClmmPools();
  const candidates = pools.filter((candidate) => {
    const ids = [candidate.currency0, candidate.currency1];
    return (
      (Number(candidate.fee) & DYNAMIC_FEE_FLAG) === 0 &&
      univ4HookAllowed(candidate.hooks) &&
      ids.includes(options.tokenInId) &&
      ids.includes(options.tokenOutId)
    );
  });
  let best: {
    contractId: string;
    pool: ClmmPoolView;
    amountOutRaw: string;
    sqrtPriceAfter: string;
  } | null = null;
  for (const pool of candidates) {
    const zeroForOne = options.tokenInId === pool.currency0;
    let view: {
      amount0: string;
      amount1: string;
      sqrt_price_x96: string;
      hooks_not_applied: boolean;
    };
    try {
      view = await wallet.account().viewFunction(contractId, 'quote_swap', {
        key: poolKeyOf(pool),
        zero_for_one: zeroForOne,
        amount_specified: options.amountInRaw,
        sqrt_price_limit_x96: extremeSqrtPriceLimit(zeroForOne),
      });
    } catch {
      continue;
    }
    if (view.hooks_not_applied) continue;
    const amountOutRaw = zeroForOne ? view.amount1 : view.amount0;
    if (!amountOutRaw || amountOutRaw.startsWith('-') || amountOutRaw === '0') {
      continue;
    }
    if (!best || BigInt(amountOutRaw) > BigInt(best.amountOutRaw)) {
      best = {
        contractId,
        pool,
        amountOutRaw,
        sqrtPriceAfter: view.sqrt_price_x96,
      };
    }
  }
  return best;
}

// Older NEP-145 tokens take 0.00125 NEAR. Newer ones (including V4TEST) take more,
// and storage_deposit panics when the attached amount is below storage_balance_bounds.min.
async function storageDepositAmount(tokenId: string) {
  try {
    const bounds = await wallet
      .account()
      .viewFunction(tokenId, 'storage_balance_bounds', {});
    const near = bounds?.min
      ? utils.format.formatNearAmount(String(bounds.min))
      : '';
    if (near && near !== '0') return near;
  } catch (error) {}
  return '0.00125';
}

const PAYOUT_KEY = 'zear_clmm_payout';
const PAYOUT_AT = 'zear_clmm_payout_at';
let claimStarted = false;

async function vaultBalance(
  contractId: string,
  accountId: string,
  tokenId: string
) {
  try {
    const balance = await wallet
      .account()
      .viewFunction(contractId, 'get_vault_balance', {
        account_id: accountId,
        token_id: tokenId,
      });
    return BigInt(vaultAmount(balance) || '0');
  } catch (error) {
    return 0n;
  }
}

function vaultAmount(value: unknown) {
  if (typeof value === 'string' || typeof value === 'number')
    return String(value);
  if (value && typeof value === 'object' && '0' in value) {
    return String((value as { '0': unknown })['0'] ?? '0');
  }
  return '0';
}

// The swap credits the output token in a later callback. Withdrawing it in the
// same wallet batch runs against an empty vault and panics.
export async function claimClmmPayout() {
  if (claimStarted) return;
  const raw = sessionStorage.getItem(PAYOUT_KEY);
  if (!raw) return;
  const prompted = Number(sessionStorage.getItem(PAYOUT_AT) || '0');
  if (Date.now() - prompted < 45000) return;
  claimStarted = true;
  try {
    const saved = JSON.parse(raw) as {
      contractId: string;
      tokenId: string;
      accountId: string;
    };
    const accountId = getCurrentWallet()?.wallet?.getAccountId();
    if (!accountId || accountId !== saved.accountId) return;
    for (let attempt = 0; attempt < 12; attempt++) {
      const balance = await wallet
        .account()
        .viewFunction(saved.contractId, 'get_vault_balance', {
          account_id: accountId,
          token_id: saved.tokenId,
        });
      const amount = vaultAmount(balance);
      if (BigInt(amount || '0') > 0n) {
        sessionStorage.setItem(PAYOUT_AT, String(Date.now()));
        await executeMultipleTransactions(
          [
            {
              receiverId: saved.contractId,
              functionCalls: [
                {
                  methodName: 'withdraw',
                  args: { token_id: saved.tokenId, amount },
                  gas: '80000000000000',
                  amount: ONE_YOCTO_NEAR,
                },
              ],
            },
          ],
          `${window.location.origin}${window.location.pathname}`
        );
        sessionStorage.removeItem(PAYOUT_KEY);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    sessionStorage.removeItem(PAYOUT_KEY);
  } catch (error) {
    sessionStorage.removeItem(PAYOUT_KEY);
  } finally {
    claimStarted = false;
  }
}

/** Wrap enough account NEAR to cover a wNEAR transfer the wallet does not already hold. */
async function wrapShortfall(
  tokenId: string,
  raw: bigint,
  transactions: any[]
) {
  const wrapId = getConfig().WRAP_NEAR_CONTRACT_ID;
  if (tokenId !== wrapId || raw <= 0n) return;
  const held = BigInt((await ftGetBalance(wrapId).catch(() => '0')) || '0');
  if (held >= raw) return;
  const accountId = getCurrentWallet()?.wallet?.getAccountId();
  const registered = await ftGetStorageBalance(wrapId).catch(() => null);
  if (registered === null && accountId) {
    transactions.unshift(
      storageRegistration(wrapId, await storageDepositAmount(wrapId), accountId)
    );
  }
  transactions.unshift(
    nearDepositTransaction(
      utils.format.formatNearAmount((raw - held).toString())
    )
  );
}

function storageRegistration(
  tokenId: string,
  amount: string,
  accountId?: string
) {
  return {
    receiverId: tokenId,
    functionCalls: [
      {
        methodName: 'storage_deposit',
        args: { account_id: accountId, registration_only: true },
        gas: '30000000000000',
        amount,
      },
    ],
  };
}

export async function swapClmm(options: {
  contractId: string;
  pool: ClmmPoolView;
  tokenInId: string;
  decimals: number;
  amount: string;
  quotedOut: string;
  sqrtPriceAfter: string;
  slippageBps: bigint;
  wrapNear?: boolean;
}) {
  const raw = toNonDivisibleNumber(options.decimals, options.amount);
  const zeroForOne = options.tokenInId === options.pool.currency0;
  const outToken = zeroForOne ? options.pool.currency1 : options.pool.currency0;
  const accountId = getCurrentWallet()?.wallet?.getAccountId();
  const owed = BigInt(raw);
  const held = accountId
    ? await vaultBalance(options.contractId, accountId, options.tokenInId)
    : 0n;
  const payRaw = held >= owed ? 0n : owed - held;
  let storageNear = 0n;
  if (accountId) {
    try {
      const stored = await wallet
        .account()
        .viewFunction(options.contractId, 'storage_balance_of', {
          account_id: accountId,
        });
      storageNear = BigInt(vaultAmount(stored) || '0');
    } catch (error) {}
  }
  const transactions: any[] = [];
  if (storageNear < 20000000000000000000000n) {
    transactions.push(depositStorage(options.contractId));
  }
  if (payRaw > 0n) {
    transactions.push(
      transfer(options.tokenInId, options.contractId, payRaw.toString())
    );
  }
  transactions.push({
    receiverId: options.contractId,
    functionCalls: [
      {
        methodName: 'swap',
        args: {
          key: poolKeyOf(options.pool),
          zero_for_one: zeroForOne,
          amount_specified: raw,
          sqrt_price_limit_x96: sqrtPriceLimit({
            startSqrt: options.pool.sqrt_price_x96,
            endSqrt: options.sqrtPriceAfter,
            zeroForOne,
            slippageBps: options.slippageBps,
          }),
          amount_out_min: options.quotedOut.replace('-', '') || '0',
          amount_in_max: raw,
        },
        gas: '300000000000000',
        amount: '0',
      },
    ],
  });
  const registered = await ftGetStorageBalance(outToken).catch(() => null);
  if (registered === null) {
    transactions.unshift(
      storageRegistration(
        outToken,
        await storageDepositAmount(outToken),
        accountId
      )
    );
  }
  if (options.wrapNear && payRaw > 0n) {
    transactions.unshift(
      nearDepositTransaction(utils.format.formatNearAmount(payRaw.toString()))
    );
    const wrapRegistered = await ftGetStorageBalance(
      getConfig().WRAP_NEAR_CONTRACT_ID
    ).catch(() => null);
    if (wrapRegistered === null) {
      transactions.unshift(
        storageRegistration(
          getConfig().WRAP_NEAR_CONTRACT_ID,
          await storageDepositAmount(getConfig().WRAP_NEAR_CONTRACT_ID),
          accountId
        )
      );
    }
  }
  if (accountId && BigInt(options.quotedOut.replace('-', '') || '0') > 0n) {
    sessionStorage.setItem(
      PAYOUT_KEY,
      JSON.stringify({
        contractId: options.contractId,
        tokenId: outToken,
        accountId,
      })
    );
  }
  await executeMultipleTransactions(
    transactions,
    `${window.location.origin}${window.location.pathname}`
  );
  await claimClmmPayout();
}

export async function removeClmmLiquidity(options: {
  contractId: string;
  pool: ClmmPoolView;
  tickLower: number;
  tickUpper: number;
  liquidity: string;
  vault0: string;
  vault1: string;
}) {
  const sqrtPrice = BigInt(options.pool.sqrt_price_x96);
  const returned = amountsForLiquidity(
    sqrtPrice,
    sqrtRatioAtTick(options.tickLower),
    sqrtRatioAtTick(options.tickUpper),
    BigInt(options.liquidity),
    false
  );
  const out0 = BigInt(options.vault0 || '0') + returned.amount0;
  const out1 = BigInt(options.vault1 || '0') + returned.amount1;
  const transactions: any[] = [
    {
      receiverId: options.contractId,
      functionCalls: [
        {
          methodName: 'modify_liquidity',
          args: {
            key: poolKeyOf(options.pool),
            tick_lower: options.tickLower,
            tick_upper: options.tickUpper,
            liquidity_delta: `-${options.liquidity}`,
            salt: '',
            amount0_max: '0',
            amount1_max: '0',
          },
          gas: '200000000000000',
          amount: '0',
        },
      ],
    },
  ];
  if (out0 > 0n) {
    transactions.push({
      receiverId: options.contractId,
      functionCalls: [
        {
          methodName: 'withdraw',
          args: { token_id: options.pool.currency0, amount: out0.toString() },
          gas: '80000000000000',
          amount: ONE_YOCTO_NEAR,
        },
      ],
    });
  }
  if (out1 > 0n) {
    transactions.push({
      receiverId: options.contractId,
      functionCalls: [
        {
          methodName: 'withdraw',
          args: { token_id: options.pool.currency1, amount: out1.toString() },
          gas: '80000000000000',
          amount: ONE_YOCTO_NEAR,
        },
      ],
    });
  }
  await executeMultipleTransactions(
    transactions,
    `${window.location.origin}${window.location.pathname}`
  );
}
